/**
 * The one column system the intake page's header and body BOTH sit on.
 *
 * The alignment complaint this exists to answer: the page used to stack three
 * different content edges on top of each other — a 960px-centred brand row, a
 * full-bleed step strip starting at the window edge, and a 720px-centred card — so
 * on a wide screen nothing lined up with anything. Applying `INTAKE_SHELL` and
 * `INTAKE_COLUMNS` to the header's inner element AND to `<main>` is what fixes that
 * structurally: the wordmark lands in the same track as the section rail, and the
 * card is the same track in both, so there is exactly one set of vertical edges on
 * the page.
 *
 * Below `lg` the shell is capped at the card's own 720px rather than the full
 * 1280, and that cap is load-bearing: without it, between `sm` and `lg` the header
 * row would span the whole 1280 shell while the card stayed a 720px centred column,
 * putting the wordmark up to ~116px left of the card's edge. That is the same
 * complaint this file exists to answer, just at a narrower window, and it is the one
 * an `lg:`-only column definition quietly leaves behind.
 *
 * The arithmetic, since the numbers look arbitrary and are not (`px-6` is 24px a
 * side, so the shell's content box is its width minus 48):
 *
 *   two columns, from `lg` (1024px):  220 + 32 + 720            =  972  <= 1024 - 48
 *   three columns, from `xl` (1280px): 220 + 32 + 720 + 32 + 220 = 1224 <= 1280 - 48
 *
 * Both fit with a few pixels to spare at the exact breakpoint, which is why the gap
 * is 32px (`gap-x-8`) rather than the 40px that reads slightly better: at 40px the
 * card track would have to give up width at 1024 and again at 1280, and the card
 * holding a steady 720px measure matters more than 8px of gutter. `minmax(0, 720px)`
 * means the card yields rather than overflowing if a future change spends the slack.
 *
 * The third column at `xl` is deliberately EMPTY and deliberately the same width as
 * the rail. Grid reserves the track whether or not anything is in it, so with
 * `justify-center` the rail-plus-card pair is balanced by an equal phantom on the
 * right and the card sits dead centre in the viewport. Between `lg` and `xl` there
 * is no phantom and the pair centres as a unit, which offsets the card by half a
 * rail (~126px); that reads as intentional weighting rather than as a mistake, and
 * it is the trade for not squeezing the card on a 1024px laptop.
 */
export const INTAKE_SHELL = 'mx-auto w-full max-w-[720px] px-4 sm:px-6 lg:max-w-[1280px]';

export const INTAKE_COLUMNS =
  'lg:grid lg:grid-cols-[220px_minmax(0,720px)] lg:justify-center lg:gap-x-8 ' +
  'xl:grid-cols-[220px_minmax(0,720px)_220px]';

/**
 * The card's grid item. It carries no width of its own: below `lg` the shell above
 * is already capped at 720 and centred, and from `lg` the grid track owns the width.
 * A second `max-w-[720px]` here would be a third place to keep in step with the
 * other two.
 */
export const INTAKE_CARD_COLUMN = 'w-full';
